import React, { useEffect, useState, useRef, Fragment } from 'react';
function getEndTime(): number {
  try {
    const saved = localStorage.getItem('megapack_countdown_end');
    if (saved) {
      const t = parseInt(saved, 10);
      if (t > Date.now()) return t;
    }
    const t = Date.now() + 23 * 3600000 + 58 * 60000 + 33000;
    localStorage.setItem('megapack_countdown_end', t.toString());
    return t;
  } catch {
    return Date.now() + 23 * 3600000 + 58 * 60000 + 33000;
  }
}
function computeDisplay(endTime: number) {
  const d = endTime - Date.now();
  if (d <= 0) return {
    h: '00',
    m: '00',
    s: '00'
  };
  return {
    h: String(Math.floor(d / 3600000 % 24)).padStart(2, '0'),
    m: String(Math.floor(d / 60000 % 60)).padStart(2, '0'),
    s: String(Math.floor(d / 1000 % 60)).padStart(2, '0')
  };
}
export function CountdownSection() {
  const endTimeRef = useRef<number>(0);
  const rootRef = useRef<HTMLElement | null>(null);
  const intervalRef = useRef<number | null>(null);
  const [display, setDisplay] = useState({
    h: '00',
    m: '00',
    s: '00'
  });
  useEffect(() => {
    const init = () => {
      endTimeRef.current = getEndTime();
      setDisplay(computeDisplay(endTimeRef.current));
    };
    const w = window as any;
    const idle = w.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 200));
    const handle = idle(init);
    return () => {
      if (w.cancelIdleCallback && typeof handle === 'number') {
        try {
          w.cancelIdleCallback(handle);
        } catch {}
      }
    };
  }, []);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const start = () => {
      if (intervalRef.current != null) return;
      if (endTimeRef.current) setDisplay(computeDisplay(endTimeRef.current));
      intervalRef.current = window.setInterval(() => {
        if (!endTimeRef.current) return;
        setDisplay(computeDisplay(endTimeRef.current));
      }, 1000);
    };
    const stop = () => {
      if (intervalRef.current != null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    if (typeof IntersectionObserver === 'undefined') {
      start();
      return stop;
    }
    const io = new IntersectionObserver(entries => {
      for (const e of entries) {
        if (e.isIntersecting) start();else stop();
      }
    }, {
      rootMargin: '0px 0px -40px 0px',
      threshold: 0.1
    });
    io.observe(el);
    const onVisibility = () => {
      if (document.hidden) stop();else if (el.getBoundingClientRect().top < window.innerHeight) start();
    };
    document.addEventListener('visibilitychange', onVisibility, {
      passive: true
    });
    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, []);
  return <section ref={rootRef} className="bg-brand-dark text-brand-ink py-20 md:py-24 px-4 relative border-t border-brand-border" data-id="element-34">
      <div className="max-w-2xl mx-auto text-center relative z-10" data-id="element-35">
        {/* Eyebrow */}
        <p className="text-brand-gold font-badge font-bold text-[11px] md:text-sm uppercase tracking-[0.2em] mb-3 md:mb-4" data-id="element-36">
          Launch price ends soon
        </p>

        <h2 className="text-2xl md:text-4xl font-heading font-bold mb-8 md:mb-10 tracking-tight text-brand-ink" data-id="element-37">
          This offer ends in
        </h2>

        {/* Digit tiles */}
        <div className="flex justify-center items-start gap-3 md:gap-5 mb-8 md:mb-10" data-id="element-38">
          {[{
          v: display.h,
          label: 'Hours'
        }, {
          v: display.m,
          label: 'Minutes'
        }, {
          v: display.s,
          label: 'Seconds'
        }].map((unit, i) => <Fragment key={unit.label} data-id="element-39">
              {i > 0 && <span className="text-4xl md:text-6xl leading-none font-light text-brand-muted/30 self-start mt-1 md:mt-2" data-id="element-40">
                  :
                </span>}
              <div className="flex flex-col items-center" data-id="element-41">
                <div className="bg-brand-card border border-brand-border rounded-xl px-3 py-3 md:px-6 md:py-5 min-w-[72px] md:min-w-[120px] shadow-sm" data-id="element-42">
                  <span className="block text-4xl md:text-7xl leading-none font-heading font-bold text-brand-accent tracking-tight tabular-nums" data-id="element-43">
                    {unit.v}
                  </span>
                </div>
                <span className="text-[10px] md:text-sm uppercase tracking-[0.15em] text-brand-muted mt-2 md:mt-3 font-medium" data-id="element-44">
                  {unit.label}
                </span>
              </div>
            </Fragment>)}
        </div>

        {/* Price-rise note */}
        <p className="text-sm md:text-lg text-brand-muted leading-relaxed mb-8 md:mb-10 max-w-lg mx-auto" data-id="element-45">
          When the timer ends, the price returns to{' '}
          <span className="text-brand-ink font-semibold" data-id="element-46">$57</span>. Lock in the
          launch price today.
        </p>

        {/* Scarcity bar */}
        <div className="max-w-md mx-auto w-full" data-id="element-47">
          <div className="flex justify-between items-center mb-2 px-0.5" data-id="element-48">
            <span className="text-brand-muted text-xs md:text-sm font-medium" data-id="element-49">
              Launch-price spots
            </span>
            <span className="text-brand-muted text-xs md:text-sm font-medium" data-id="element-50">
              88% claimed
            </span>
          </div>
          <div className="w-full h-2 md:h-2.5 bg-brand-border rounded-full overflow-hidden" data-id="element-51">
            <div className="h-full rounded-full bg-brand-accent" style={{
            width: '88%'
          }} data-id="element-52" />
          </div>
        </div>
      </div>
    </section>;
}